import { useState } from 'preact/hooks';

interface MessageTemplate {
  id: string;
  template: string;
  description: string | null;
}

interface MessagePreviewProps {
  template: MessageTemplate;
  onSave: (id: string, template: string) => void;
}

// Example values for template placeholders
const EXAMPLE_VALUES: Record<string, string> = {
  content: '🟢 Gateway: OK\n🟢 BM-613: OK\n🟢 Mail: OK',
  level: '12',
  rule_id: '5502',
  description: 'Login brute force attempt detected',
  agent: 'gateway',
  agent_id: '001',
  timestamp: '2026-07-16 09:15:33',
  event_type: 'file_integrity',
  resource: '/etc/shadow',
  severity: 'HIGH',
  server: 'BM-613',
};

/**
 * Replace all {{placeholder}} with example values.
 */
function renderPreview(template: string): string {
  return template.replace(/\{\{(\w+)\}\}/g, (_, key: string) => {
    return EXAMPLE_VALUES[key] ?? `[${key}]`;
  });
}

export function MessagePreview({ template, onSave }: MessagePreviewProps) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState(template.template);
  const [dirty, setDirty] = useState(false);

  const preview = renderPreview(editing ? draft : template.template);

  const handleEdit = () => {
    setDraft(template.template);
    setEditing(true);
    setDirty(false);
  };

  const handleSave = () => {
    onSave(template.id, draft);
    setEditing(false);
    setDirty(false);
  };

  const handleCancel = () => {
    setDraft(template.template);
    setEditing(false);
    setDirty(false);
  };

  return (
    <div class="message-template-card">
      <div class="template-header">
        <h3 class="template-id">{template.id}</h3>
        {template.description && (
          <span class="template-desc">{template.description}</span>
        )}
      </div>

      {editing ? (
        <div class="template-editor">
          <textarea
            class="template-textarea"
            value={draft}
            onInput={(e) => {
              setDraft((e.target as HTMLTextAreaElement).value);
              setDirty(true);
            }}
            rows={6}
          />
          <div class="template-actions">
            <button class="btn btn-primary" onClick={handleSave} disabled={!dirty}>
              Speichern
            </button>
            <button class="btn btn-secondary" onClick={handleCancel}>
              Abbrechen
            </button>
          </div>
        </div>
      ) : (
        <div class="template-view" onClick={handleEdit}>
          <span class="edit-hint">Klicken zum Bearbeiten</span>
        </div>
      )}

      <div class="template-preview">
        <h4 class="preview-label">Vorschau:</h4>
        <div class="preview-bubble">{preview}</div>
      </div>
    </div>
  );
}
